import { useEffect, useState, useRef } from "react";
import axios, { API } from "../../api";
import { LiveListenPlayer } from "../../liveListenPlayer";
import { ScreenRecorder } from "../../screenRecorder";

/**
 * Phase 26 — Aura Outbound Calls modal.
 *
 * Users with Premium can have Aura place a real outbound phone call:
 *   - Templates: Prank, Check-in, Birthday, Hype
 *   - 10-minute hard cap (Bland.ai max_duration)
 *   - HD voice toggle → 4x minute cost
 *
 * Flow:
 *   1. User fills phone + recipient name + optional note + picks template + HD toggle.
 *   2. POST /api/calls/start → returns {id, bland_call_id, status: "queued"}.
 *   3. Frontend polls /api/calls/status/{id} every 5s until completed/failed/no-answer.
 *   4. Past calls listed below the form, newest first.
 */

const TEMPLATES = [
  { id: "check_in", label: "Check-in", emoji: "💚", desc: "How are you really doing?" },
  { id: "birthday", label: "Birthday",  emoji: "🎂", desc: "Wish someone happy birthday." },
  { id: "hype",     label: "Hype",      emoji: "🚀", desc: "A motivational pep talk." },
  { id: "prank",    label: "Prank",     emoji: "😜", desc: "Playful, never mean." },
];

export function OutboundCallsModal({ onClose, user }) {
  const [template, setTemplate] = useState("check_in");
  const [phone, setPhone] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [customNote, setCustomNote] = useState("");
  const [hdVoice, setHdVoice] = useState(false);
  // Phase 28 — Live listen-in
  const [listenLive, setListenLive] = useState(false);
  const [disclosureWhen, setDisclosureWhen] = useState("start"); // "start" | "end" | "never"
  const [customReveal, setCustomReveal] = useState("");
  const [listenState, setListenState] = useState("idle"); // idle|connecting|playing|ended|error
  const playerRef = useRef(null);
  // Phase 28B — Creator Studio screen recording
  const [recording, setRecording] = useState(false);
  const [recError, setRecError] = useState("");
  const recorderRef = useRef(null);
  const isCreator = !!user?.creator_studio_active;
  const [balance, setBalance] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [activeCall, setActiveCall] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);
  // Phase 29.D — Prank checklist (required when template === "prank")
  const [prankPetPeeves, setPrankPetPeeves] = useState("");
  const [prankOffLimits, setPrankOffLimits] = useState("");
  const [prankBailThreshold, setPrankBailThreshold] = useState("");
  // Phase 29.E — Break-character decision state (one-shot per call)
  const [breakDecisionPending, setBreakDecisionPending] = useState(false);
  const [breakDecisionSent, setBreakDecisionSent] = useState(null);

  // When the user switches template, pick the most natural disclosure default:
  //   prank → "end" reveal (don't blow the joke)
  //   everything else → "start" (transparent, friendly)
  useEffect(() => {
    setDisclosureWhen(template === "prank" ? "end" : "start");
  }, [template]);

  const isPremium = !!user?.has_premium;
  const minutesNeeded = 10 * (hdVoice ? 4 : 1);

  const loadBalance = async () => {
    try {
      const res = await axios.get(`${API}/voice/balance`);
      setBalance(res.data);
    } catch (e) { /* noop */ }
  };
  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/calls?limit=20`);
      setHistory(res.data?.calls || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    loadBalance();
    loadHistory();
  }, []);

  // Poll the active call's status while it's not in a terminal state.
  useEffect(() => {
    if (!activeCall?.id) return;
    const terminal = ["completed", "failed", "no-answer", "cancelled", "canceled"];
    if (terminal.includes(activeCall.status)) {
      // Stop the player if it was running.
      if (playerRef.current) {
        try { playerRef.current.stop(); } catch (_) { /* ignore */ }
        playerRef.current = null;
      }
      return;
    }

    const tick = async () => {
      try {
        const res = await axios.get(`${API}/calls/status/${activeCall.id}`);
        setActiveCall((c) => ({ ...c, ...res.data }));
        // Phase 29.E — first poll that shows prank_broke_character_at triggers
        // the banner. We don't auto-fire again on subsequent polls because the
        // user's choice (or call end) clears it.
        if (
          res.data.prank_broke_character_at &&
          !res.data.prank_break_user_decision &&
          !breakDecisionSent
        ) {
          setBreakDecisionPending(true);
        }
        if (terminal.includes(res.data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (playerRef.current) {
            try { playerRef.current.stop(); } catch (_) { /* ignore */ }
            playerRef.current = null;
          }
          loadBalance();
          loadHistory();
        }
      } catch (e) { /* keep polling */ }
    };
    pollRef.current = setInterval(tick, 5000);
    tick();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeCall?.id, activeCall?.status]);

  // Phase 28 — When the call is live AND listen_live was requested AND we
  // don't yet have a player running, fetch the WS URL and start streaming.
  useEffect(() => {
    if (!activeCall?.id) return;
    if (!activeCall.listen_live && !listenLive) return;
    const liveStates = ["queued", "in-progress"];
    if (!liveStates.includes(activeCall.status)) return;
    if (playerRef.current) return; // already running

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/calls/listen/${activeCall.id}`);
        if (cancelled) return;
        const wsUrl = res.data?.ws_url;
        if (!wsUrl) return;
        const player = new LiveListenPlayer(wsUrl, {
          onStateChange: setListenState,
        });
        playerRef.current = player;
        await player.start();
      } catch (e) {
        // 409 = call not connected yet; will retry on next poll tick
      }
    })();
    return () => { cancelled = true; };
  }, [activeCall?.id, activeCall?.status, activeCall?.listen_live, listenLive]);

  const handleStart = async () => {
    setError("");
    if (!phone.trim()) { setError("Enter a phone number."); return; }
    if (!phone.startsWith("+")) { setError("Phone must include country code (e.g. +1...)."); return; }
    // Phase 29.D — Local guard so the user gets immediate feedback instead of
    // waiting for the server 400. Server still re-validates.
    if (template === "prank") {
      if (!prankPetPeeves.trim() || !prankOffLimits.trim() || !prankBailThreshold.trim()) {
        setError("Fill out the full prank checklist before placing the call.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/calls/start`, {
        phone_number: phone.trim(),
        template,
        recipient_name: recipientName.trim() || null,
        custom_note: customNote.trim() || null,
        hd_voice: hdVoice,
        listen_live: listenLive,
        disclose_listener_at: listenLive ? disclosureWhen : null,
        custom_reveal_text: (listenLive && disclosureWhen === "end" && customReveal.trim())
          ? customReveal.trim() : null,
        prank_pet_peeves: template === "prank" ? prankPetPeeves.trim() : null,
        prank_off_limits: template === "prank" ? prankOffLimits.trim() : null,
        prank_bail_threshold: template === "prank" ? prankBailThreshold.trim() : null,
      });
      setActiveCall(res.data);
      setBreakDecisionPending(false);
      setBreakDecisionSent(null);
      loadBalance();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't place that call.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActiveCall(null);
    setError("");
    setPhone("");
    setRecipientName("");
    setCustomNote("");
  };

  // Phase 28B — Creator Studio: screen recording controls
  const startScreenRec = async () => {
    setRecError("");
    try {
      const rec = new ScreenRecorder();
      await rec.start({ withMic: false });
      recorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      setRecError(e?.message || "Screen recording was canceled or blocked.");
    }
  };
  const stopScreenRec = async () => {
    if (!recorderRef.current) return;
    try {
      const blob = await recorderRef.current.stop();
      recorderRef.current = null;
      setRecording(false);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `aura-call-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (e) {
      setRecError(e?.message || "Couldn't save recording.");
    }
  };

  // Phase 28B — Creator Studio: download Bland MP3 + transcript for a past call
  const downloadCallRecording = async (callId) => {
    try {
      const res = await axios.get(`${API}/calls/recording/${callId}`);
      const { recording_url, transcript } = res.data;
      if (recording_url) {
        window.open(recording_url, "_blank", "noopener,noreferrer");
      }
      if (transcript) {
        const blob = new Blob([transcript], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `aura-call-${callId}-transcript.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't fetch recording yet — Bland may still be processing it.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="outbound-calls-overlay">
      <div className="outbound-calls-modal" onClick={(e) => e.stopPropagation()}>
        <div className="journal-header">
          <h2>📞 Have Aura Make a Call</h2>
          <button className="journal-close" onClick={onClose} data-testid="outbound-calls-close">×</button>
        </div>

        {!isPremium && (
          <div className="lock-banner" data-testid="outbound-locked-banner">
            🔒 Outbound calls are a Premium feature. Upgrade to unlock.
          </div>
        )}

        {balance && (
          <div className="outbound-balance" data-testid="outbound-balance">
            <span>You have <strong>{balance.total_available}</strong> Aura voice minutes.</span>
            <span className="outbound-balance-hint">
              This call needs ≥{minutesNeeded} min{hdVoice ? " (HD = 4×)" : ""}
            </span>
          </div>
        )}

        {!activeCall && (
          <div className="outbound-form">
            <label className="outbound-label">Who's calling?</label>
            <div className="outbound-template-grid">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`outbound-template-card ${template === t.id ? "active" : ""}`}
                  onClick={() => setTemplate(t.id)}
                  data-testid={`outbound-tpl-${t.id}`}
                >
                  <span className="outbound-template-emoji">{t.emoji}</span>
                  <span className="outbound-template-label">{t.label}</span>
                  <span className="outbound-template-desc">{t.desc}</span>
                </button>
              ))}
            </div>

            <label className="outbound-label">Phone number (E.164)</label>
            <input
              type="tel"
              className="init-input"
              placeholder="+14155551234"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="outbound-phone-input"
              disabled={!isPremium}
            />

            <label className="outbound-label">Their name (optional)</label>
            <input
              type="text"
              className="init-input"
              placeholder="e.g. Alex"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              data-testid="outbound-recipient-input"
              disabled={!isPremium}
            />

            <label className="outbound-label">Anything Aura should know? (optional)</label>
            <textarea
              className="init-input outbound-note"
              placeholder="e.g. They just got engaged — congratulate them"
              value={customNote}
              onChange={(e) => setCustomNote(e.target.value)}
              maxLength={400}
              data-testid="outbound-note-input"
              disabled={!isPremium}
            />

            {/* Phase 29.D — Prank checklist (required for prank template) */}
            {template === "prank" && (
              <div className="prank-checklist" data-testid="prank-checklist">
                <div className="prank-checklist-header">
                  <span>😜 Prank checklist</span>
                  <span className="prank-checklist-required">required</span>
                </div>
                <div className="prank-checklist-hint">
                  Quick sanity check so this stays funny. All three are required.
                </div>
                <label className="outbound-label">
                  Pet peeves or topics that would actually upset them
                </label>
                <textarea
                  className="init-input outbound-note"
                  placeholder="e.g. anything about their weight, their ex Jamie, money"
                  value={prankPetPeeves}
                  onChange={(e) => setPrankPetPeeves(e.target.value)}
                  maxLength={280}
                  data-testid="prank-pet-peeves-input"
                  disabled={!isPremium}
                />
                <label className="outbound-label">
                  Things Aura must NEVER say
                </label>
                <textarea
                  className="init-input outbound-note"
                  placeholder="e.g. don't mention their mom, don't pretend it's an emergency"
                  value={prankOffLimits}
                  onChange={(e) => setPrankOffLimits(e.target.value)}
                  maxLength={280}
                  data-testid="prank-off-limits-input"
                  disabled={!isPremium}
                />
                <label className="outbound-label">
                  When should Aura bail and end the call?
                </label>
                <textarea
                  className="init-input outbound-note"
                  placeholder="e.g. if they sound upset, or after 90 seconds, or if they ask seriously"
                  value={prankBailThreshold}
                  onChange={(e) => setPrankBailThreshold(e.target.value)}
                  maxLength={200}
                  data-testid="prank-bail-input"
                  disabled={!isPremium}
                />
              </div>
            )}

            <label className="outbound-hd-toggle" data-testid="outbound-hd-toggle">
              <input
                type="checkbox"
                checked={hdVoice}
                onChange={(e) => setHdVoice(e.target.checked)}
                disabled={!isPremium}
              />
              <span>
                <strong>HD voice</strong> · Premium, more natural · <em>charges 4× minutes</em>
              </span>
            </label>

            {/* Phase 28 — Live listen-in (free, all premium tiers) */}
            <label className="outbound-hd-toggle" data-testid="outbound-listen-toggle">
              <input
                type="checkbox"
                checked={listenLive}
                onChange={(e) => setListenLive(e.target.checked)}
                disabled={!isPremium}
              />
              <span>
                <strong>🎧 Listen in live</strong> · Hear both sides in real time
              </span>
            </label>

            {listenLive && (
              <div className="outbound-disclosure-box" data-testid="outbound-disclosure-box">
                <div className="outbound-disclosure-label">
                  When should Aura tell them you're listening?
                </div>
                <div className="outbound-disclosure-options">
                  <label>
                    <input
                      type="radio"
                      name="disclosure"
                      value="start"
                      checked={disclosureWhen === "start"}
                      onChange={() => setDisclosureWhen("start")}
                      data-testid="disclosure-start"
                    />
                    <span>Right away (transparent)</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="disclosure"
                      value="end"
                      checked={disclosureWhen === "end"}
                      onChange={() => setDisclosureWhen("end")}
                      data-testid="disclosure-end"
                    />
                    <span>At the end (good for pranks/streams)</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="disclosure"
                      value="never"
                      checked={disclosureWhen === "never"}
                      onChange={() => setDisclosureWhen("never")}
                      data-testid="disclosure-never"
                    />
                    <span>Never (you accept the liability)</span>
                  </label>
                </div>
                {disclosureWhen === "end" && (
                  <>
                    <textarea
                      className="init-input outbound-note"
                      placeholder="Your reveal line — e.g. &quot;You just got pranked by Jerry's Aura! How'd I do?!&quot;"
                      value={customReveal}
                      onChange={(e) => setCustomReveal(e.target.value)}
                      maxLength={250}
                      data-testid="outbound-reveal-input"
                    />
                    <div className="outbound-disclosure-hint">
                      Aura will read this word-for-word at the end. Leave blank for her default.
                    </div>
                  </>
                )}
                <div className="outbound-disclosure-hint">
                  ⚖️ Aura will always say "this call may be recorded" up front — that satisfies most state laws.
                </div>
              </div>
            )}

            {error && <div className="error-message" data-testid="outbound-error">{error}</div>}

            <button
              className="init-btn"
              onClick={handleStart}
              disabled={!isPremium || submitting}
              data-testid="outbound-start-btn"
            >
              {submitting ? "Dialing..." : "📞 Place call"}
            </button>
          </div>
        )}

        {activeCall && (
          <div className="outbound-active" data-testid="outbound-active-call">
            <div className="outbound-active-status">
              <strong>{activeCall.template_label || "Aura call"}</strong>
              <span className={`outbound-status-pill outbound-status-${activeCall.status}`}>
                {activeCall.status === "queued" && "📲 Dialing..."}
                {activeCall.status === "in-progress" && "🎙️ On the call..."}
                {activeCall.status === "completed" && "✅ Done"}
                {activeCall.status === "no-answer" && "📵 No answer"}
                {activeCall.status === "failed" && "⚠️ Failed"}
                {!["queued","in-progress","completed","no-answer","failed"].includes(activeCall.status)
                  && activeCall.status}
              </span>
            </div>
            {activeCall.duration_seconds != null && (
              <div className="outbound-active-meta">
                Duration: {Math.round(activeCall.duration_seconds)}s
                {activeCall.minutes_charged != null && ` · Charged ${activeCall.minutes_charged} min`}
              </div>
            )}

            {/* Phase 29.E — Break-character banner. Appears when Aura's
                transcript line crosses the documented break phrase. */}
            {breakDecisionPending && !breakDecisionSent && (
              <div className="break-character-banner" data-testid="break-character-banner">
                <div className="break-character-text">
                  <strong>😂 Aura broke character.</strong>
                  <span> She heard too much laughter and slipped a giggle in.</span>
                  <div className="break-character-sub">
                    If they noticed, ending the call is the gracious play. If they didn't,
                    she'll return to script.
                  </div>
                </div>
                <div className="break-character-actions">
                  <button
                    type="button"
                    className="break-character-end"
                    data-testid="break-character-end-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/calls/${activeCall.id}/break-decision`, { decision: "end" });
                        setBreakDecisionSent("end");
                        setBreakDecisionPending(false);
                      } catch (_) { /* ignore */ }
                    }}
                  >
                    📵 End the call
                  </button>
                  <button
                    type="button"
                    className="break-character-continue"
                    data-testid="break-character-continue-btn"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/calls/${activeCall.id}/break-decision`, { decision: "continue" });
                        setBreakDecisionSent("continue");
                        setBreakDecisionPending(false);
                      } catch (_) { /* ignore */ }
                    }}
                  >
                    🎭 Keep going
                  </button>
                </div>
              </div>
            )}
            {breakDecisionSent && (
              <div className="break-character-resolved" data-testid="break-character-resolved">
                {breakDecisionSent === "end" ? "📵 Ending call..." : "🎭 Aura's back in character."}
              </div>
            )}
            {(activeCall.listen_live || listenLive) && listenState !== "ended" && (
              <div className={`outbound-listen-indicator listen-${listenState}`} data-testid="outbound-listen-indicator">
                {listenState === "playing" && <><span className="listen-dot" /> 🎧 Listening live...</>}
                {listenState === "connecting" && "🎧 Connecting audio..."}
                {listenState === "idle" && "🎧 Live audio will start when they pick up"}
                {listenState === "error" && "⚠️ Couldn't connect to audio (call still active)"}
              </div>
            )}
            {activeCall.error && (
              <div className="outbound-active-error">{activeCall.error}</div>
            )}
            {activeCall.transcript && (
              <details className="outbound-transcript">
                <summary>📝 Transcript</summary>
                <pre>{activeCall.transcript}</pre>
              </details>
            )}

            {isCreator && (
              <div className="cs-rec-row" data-testid="cs-rec-row">
                {!recording ? (
                  <button
                    className="init-btn cs-rec-btn"
                    onClick={startScreenRec}
                    data-testid="cs-rec-start"
                  >
                    🎬 Record screen
                  </button>
                ) : (
                  <button
                    className="init-btn cs-rec-btn rec-active"
                    onClick={stopScreenRec}
                    data-testid="cs-rec-stop"
                  >
                    ⏹️ Stop recording
                  </button>
                )}
                {recError && <span className="cs-rec-error">{recError}</span>}
              </div>
            )}

            <button
              className="init-btn"
              onClick={reset}
              data-testid="outbound-newcall-btn"
            >
              Place another call
            </button>
          </div>
        )}

        {history.length > 0 && (
          <div className="outbound-history" data-testid="outbound-history">
            <h3>Recent calls</h3>
            <div className="outbound-history-list">
              {history.map((c) => (
                <div key={c.id} className="outbound-history-row" data-testid={`outbound-history-${c.id}`}>
                  <span className="outbound-history-tpl">{c.template_label}</span>
                  <span className="outbound-history-phone">{c.phone_number}</span>
                  <span className="outbound-history-status">{c.status}</span>
                  <span className="outbound-history-min">
                    {c.minutes_charged != null ? `${c.minutes_charged}m` : "—"}
                  </span>
                  {isCreator && c.status === "completed" && (
                    <button
                      type="button"
                      className="cs-dl-btn"
                      onClick={() => downloadCallRecording(c.id)}
                      title="Download recording + transcript"
                      data-testid={`cs-dl-${c.id}`}
                    >
                      ⬇️
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
